/**
 * Binding the markup's inline handlers, without weakening the content
 * security policy.
 *
 * The three designs drive everything from `onclick` attributes, which is
 * a perfectly good way to write a page - except that the browser will
 * only run an inline handler if the policy contains `unsafe-inline`, and
 * `unsafe-inline` is exactly the thing that turns a single injected
 * string anywhere on the page into arbitrary script execution. On a page
 * that shows card numbers and moves money, that is not a trade worth
 * making.
 *
 * So the attributes stay in the markup as the design intends, and this
 * module reads them, parses them with a deliberately tiny grammar, and
 * attaches real listeners. Nothing is ever passed to `eval` or to the
 * Function constructor: an expression that does not fit the grammar is
 * refused and reported, rather than executed by some looser route.
 *
 * The grammar is only as large as the markup needs:
 *
 *     name(args)                          a call
 *     if(event.target===this) name(args)  the backdrop-dismiss idiom
 *     'text' | 12 | true | null           literals
 *     this | this.checked | this.value    the element
 *     name                                another registered function
 *     cond ? 'a' : 'b'                    a ternary on this.checked
 *     'a' + expr                          concatenation
 */

export type Registry = Record<string, unknown>;

interface Ctx {
  el: HTMLElement;
  event: Event;
  registry: Registry;
}

class ParseError extends Error {}

/* ------------------------------ tokenising ------------------------------- */

type Token = { t: 'name' | 'string' | 'number' | 'punct'; v: string };

function tokenise(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (/\s/.test(c)) { i++; continue; }
    if (c === "'" || c === '"') {
      let s = '';
      i++;
      while (i < src.length && src[i] !== c) {
        if (src[i] === '\\') { s += src[i + 1]; i += 2; continue; }
        s += src[i++];
      }
      i++;
      out.push({ t: 'string', v: s });
      continue;
    }
    if (/[0-9]/.test(c)) {
      let s = '';
      while (i < src.length && /[0-9.]/.test(src[i])) s += src[i++];
      out.push({ t: 'number', v: s });
      continue;
    }
    if (/[A-Za-z_$]/.test(c)) {
      let s = '';
      while (i < src.length && /[\w$]/.test(src[i])) s += src[i++];
      out.push({ t: 'name', v: s });
      continue;
    }
    // Multi-character operators first, so === is not read as = = =.
    const three = src.slice(i, i + 3);
    if (three === '===' || three === '!==') { out.push({ t: 'punct', v: three }); i += 3; continue; }
    if ('()?:+,.;'.includes(c)) { out.push({ t: 'punct', v: c }); i++; continue; }
    throw new ParseError(`unexpected character ${c}`);
  }
  return out;
}

/* ------------------------------- evaluating ------------------------------ */

class Reader {
  private tokens: Token[];
  private pos = 0;
  constructor(tokens: Token[]) { this.tokens = tokens; }
  peek(): Token | undefined { return this.tokens[this.pos]; }
  next(): Token { const t = this.tokens[this.pos++]; if (!t) throw new ParseError('unexpected end'); return t; }
  eat(v: string): boolean { if (this.peek()?.v === v) { this.pos++; return true; } return false; }
  expect(v: string): void { if (!this.eat(v)) throw new ParseError(`expected ${v}`); }
  get done(): boolean { return this.pos >= this.tokens.length; }
}

function evalExpression(r: Reader, ctx: Ctx): unknown {
  let left = evalConcat(r, ctx);
  if (r.eat('?')) {
    const whenTrue = evalExpression(r, ctx);
    r.expect(':');
    const whenFalse = evalExpression(r, ctx);
    return left ? whenTrue : whenFalse;
  }
  if (r.peek()?.v === '===' || r.peek()?.v === '!==') {
    const op = r.next().v;
    const right = evalConcat(r, ctx);
    return op === '===' ? left === right : left !== right;
  }
  return left;
}

function evalConcat(r: Reader, ctx: Ctx): unknown {
  let value = evalPrimary(r, ctx);
  while (r.peek()?.v === '+') {
    r.next();
    value = String(value) + String(evalPrimary(r, ctx));
  }
  return value;
}

function evalPrimary(r: Reader, ctx: Ctx): unknown {
  if (r.eat('(')) {
    const value = evalExpression(r, ctx);
    r.expect(')');
    return value;
  }
  const token = r.next();
  if (token.t === 'string') return token.v;
  if (token.t === 'number') return Number(token.v);
  if (token.t !== 'name') throw new ParseError(`unexpected ${token.v}`);

  if (token.v === 'true') return true;
  if (token.v === 'false') return false;
  if (token.v === 'null') return null;
  if (token.v === 'undefined') return undefined;

  if (token.v === 'this' || token.v === 'event') {
    let value: unknown = token.v === 'this' ? ctx.el : ctx.event;
    while (r.eat('.')) {
      const prop = r.next().v;
      // A short allowlist rather than arbitrary property access: these are
      // the only members the markup reaches for, and refusing the rest
      // means a future handler cannot quietly reach further.
      const allowed = ['checked', 'value', 'target', 'textContent', 'dataset', 'id'];
      if (!allowed.includes(prop)) throw new ParseError(`property ${prop} is not allowed`);
      value = (value as Record<string, unknown>)?.[prop];
    }
    return value;
  }

  const fn = ctx.registry[token.v];
  if (r.peek()?.v === '(') {
    r.next();
    const args: unknown[] = [];
    if (!r.eat(')')) {
      do { args.push(evalExpression(r, ctx)); } while (r.eat(','));
      r.expect(')');
    }
    if (typeof fn !== 'function') throw new ParseError(`${token.v} is not a registered handler`);
    return (fn as (...a: unknown[]) => unknown)(...args);
  }
  // A bare name is a function reference, as in faceThen(doSend).
  if (fn === undefined) throw new ParseError(`${token.v} is not registered`);
  return fn;
}

function runStatements(src: string, ctx: Ctx): void {
  const r = new Reader(tokenise(src));
  while (!r.done) {
    if (r.eat(';')) continue;
    // The one control-flow form the markup uses: dismiss on a backdrop click.
    if (r.peek()?.v === 'if') {
      r.next();
      r.expect('(');
      const condition = evalExpression(r, ctx);
      r.expect(')');
      if (!condition) return;
      continue;
    }
    evalExpression(r, ctx);
  }
}

/* -------------------------------- binding -------------------------------- */

const EVENTS = ['click', 'change', 'input', 'submit'] as const;

/**
 * Attach every inline handler in the document as a real listener.
 *
 * Safe to call more than once: the attribute is removed as it is bound,
 * so a second pass over freshly inserted markup binds only the new nodes.
 */
export function bindInline(registry: Registry, root: ParentNode = document): void {
  for (const name of EVENTS) {
    const attribute = 'on' + name;
    for (const node of Array.from(root.querySelectorAll<HTMLElement>(`[${attribute}]`))) {
      const source = node.getAttribute(attribute) ?? '';
      node.removeAttribute(attribute);
      node.addEventListener(name, (event) => {
        try {
          runStatements(source, { el: node, event, registry });
        } catch (e) {
          // Loud in the console, quiet on screen: a handler that cannot be
          // parsed is a bug in this repository, not something a customer
          // standing at a till can do anything about.
          console.error(`inline handler failed: ${source}`, e);
        }
      });
    }
  }
}

/**
 * Check one handler without a DOM.
 *
 * Used by the build test, so a handler that names a function nobody
 * registered is caught in a test run rather than by somebody pressing a
 * button at a till. Returns null when the handler is fine.
 */
export function staticCheck(source: string, registered: Set<string>): string | null {
  const builtIn = new Set(['this', 'event', 'if', 'true', 'false', 'null', 'undefined',
    'checked', 'value', 'target', 'textContent', 'dataset', 'id']);
  let tokens: Token[];
  try {
    tokens = tokenise(source);
  } catch (e) {
    return `cannot parse: ${(e as Error).message}`;
  }
  let sawCall = false;
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (token.t !== 'name' || builtIn.has(token.v)) continue;
    // A name after a dot is a property, checked by the allowlist at run time.
    if (tokens[i - 1]?.v === '.') continue;
    if (!registered.has(token.v)) return `${token.v} is not registered`;
    if (tokens[i + 1]?.v === '(') sawCall = true;
  }
  return sawCall ? null : 'does not call anything';
}

/** Report any handler in the document that would fail. */
export function checkInline(registry: Registry, root: ParentNode = document): string[] {
  const registered = new Set(Object.keys(registry));
  const problems: string[] = [];
  for (const name of EVENTS) {
    for (const node of Array.from(root.querySelectorAll<HTMLElement>(`[on${name}]`))) {
      const source = node.getAttribute('on' + name) ?? '';
      const problem = staticCheck(source, registered);
      if (problem) problems.push(`${source}: ${problem}`);
    }
  }
  return problems;
}
